if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.fitPhoto = this.fitPhoto.bind(this);
        this.fitPhotoSoon = this.fitPhotoSoon.bind(this);
      }

      hide(preventFocus = false) {
        document.removeEventListener('keydown', this.onDocumentKeydown, true);
        if (this.soldOutObserver) this.soldOutObserver.disconnect();
        this.unwatchPhoto();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        this.fetchProduct(opener)
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.rememberSection(opener, this.productElement.dataset.section);
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
            this.watchPhoto();
            this.keepFocusInside();
            this.askForRating();
            this.watchSoldOut();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /*
        AFTER FIVE: the panel shows one section of the product page, so it asks for that section
        alone (Section Rendering API) instead of the whole page (about 500 KB of HTML).
        The section's id belongs to the product template and cannot be known from a collection
        page, so the first panel of a visit for each template still fetches the page and the id
        found in it is kept for the session; later panels fetch only the section. If that request
        fails or brings no product section, the panel falls back to the whole page.
      */
      sectionKey(opener) {
        const theme = (window.Shopify && Shopify.theme && Shopify.theme.id) || '';
        return `afQuickAddSection:${theme}:${opener.getAttribute('data-product-template') || ''}`;
      }

      rememberSection(opener, sectionId) {
        if (!sectionId) return;
        try {
          sessionStorage.setItem(this.sectionKey(opener), sectionId);
        } catch (e) {}
      }

      fetchProduct(opener) {
        const pageUrl = opener.getAttribute('data-product-url');
        const wholePage = () => fetch(pageUrl).then((response) => response.text());
        let sectionId = null;
        try {
          sectionId = sessionStorage.getItem(this.sectionKey(opener));
        } catch (e) {}
        if (!sectionId) return wholePage();
        const sectionUrl = `${pageUrl}${pageUrl.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
        return fetch(sectionUrl)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error('section'))))
          .then((text) => (text.includes('id="MainProduct-') ? text : Promise.reject(new Error('section'))))
          .catch(() => {
            try {
              sessionStorage.removeItem(this.sectionKey(opener));
            } catch (e) {}
            return wholePage();
          });
      }

      /*
        AFTER FIVE: the panel's first screen is the whole photograph, the name and price, the
        options and the button, with nothing to scroll (owner, 3 Oct 2026). The photograph takes
        the height the panel has left once everything from the name to the button is placed.
        That remainder differs per product (one option row or two, a rating line, a name on two
        lines) and per phone, so it is measured here and handed to assets/quick-add.css as
        --af-quick-photo-h; the stylesheet keeps it between 200px and the full-width photograph.
        Measured again when the panel's content changes size (the rating arrives, another colour
        brings other sizes), when one of the section's stylesheets lands (they arrive with the
        section, after the first measurement) and when the panel itself changes (the phone is
        turned, the browser's bar slides away). What is under the photograph does not depend on
        the photograph's height, so a pass that finds the same remainder changes nothing and the
        observer comes to rest.
      */
      fitPhoto() {
        const scroller = this.modalContent;
        const photo = scroller.querySelector('.product__media-item:first-child .product-media-container');
        const button = scroller.querySelector('.product-form__submit');
        if (!photo || !button || !this.hasAttribute('open')) return;
        const style = getComputedStyle(scroller);
        const room = scroller.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        // Places as the layout has them, not as they are drawn at this instant: the panel and its
        // content slide in while this runs, and a measurement of the moving boxes came out short
        const root = scroller.offsetParent;
        const top = (element) => {
          let y = 0;
          for (let node = element; node && node !== root; node = node.offsetParent) y += node.offsetTop;
          return y;
        };
        const below = top(button) + button.offsetHeight - (top(photo) + photo.offsetHeight);
        const height = Math.floor(room - below);
        if (!(height > 0) || height === this.photoHeight) return;
        this.photoHeight = height;
        this.style.setProperty('--af-quick-photo-h', `${height}px`);
      }

      // From an observer the measurement waits for the next frame (a resize observer may not
      // change the size of what it observes within its own pass)
      fitPhotoSoon() {
        cancelAnimationFrame(this.photoFrame);
        this.photoFrame = requestAnimationFrame(this.fitPhoto);
      }

      watchPhoto() {
        this.unwatchPhoto();
        this.fitPhoto();
        window.addEventListener('resize', this.fitPhoto);
        this.modalContent.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          link.addEventListener('load', this.fitPhoto, { once: true });
        });
        if ('ResizeObserver' in window) {
          this.photoObserver = new ResizeObserver(this.fitPhotoSoon);
          this.modalContent.querySelectorAll('.product, .product__info-wrapper').forEach((box) => {
            this.photoObserver.observe(box);
          });
        }
      }

      unwatchPhoto() {
        window.removeEventListener('resize', this.fitPhoto);
        if (this.photoObserver) this.photoObserver.disconnect();
        cancelAnimationFrame(this.photoFrame);
        this.photoObserver = null;
        this.photoHeight = null;
        this.style.removeProperty('--af-quick-photo-h');
      }

      /*
        AFTER FIVE: a sold-out choice leads somewhere (DESIGN-TOKENS 7.5). On the product page a
        sold-out size offers "Notify me" (back-in-stock app); the app does not render inside the
        panel, which left a disabled "Sold out" button and nothing else. While the panel's button
        is disabled, a copy of the panel's "View full details" link stands directly under it and
        opens the product page with that variant chosen, where "Notify me" is.
      */
      watchSoldOut() {
        const update = () => {
          const button = this.modalContent.querySelector('.product-form__submit');
          const details = this.modalContent.querySelector('.product__view-details');
          if (!button || !details) return;
          let next = this.modalContent.querySelector('.quick-add-modal__next');
          // The button is also disabled for a moment after every choice, while the new price and
          // stock are fetched; its label says when it has settled on "Sold out" or "Unavailable"
          const strings = window.variantStrings || {};
          const label = (button.querySelector('span') || button).textContent.trim();
          const noSale = button.disabled && (label === strings.soldOut || label === strings.unavailable);
          if (!noSale) {
            if (next && !next.hidden) next.hidden = true;
            return;
          }
          let reveal = false;
          if (!next) {
            next = document.createElement('a');
            next.className = 'af-link quick-add-modal__next';
            next.textContent = details.textContent.trim();
            (button.closest('.product-form__buttons') || button).insertAdjacentElement('afterend', next);
            reveal = true;
          }
          const url = new URL(details.getAttribute('href'), window.location.href);
          const variant = button.closest('form') && button.closest('form').querySelector('input[name="id"]');
          if (variant && variant.value) url.searchParams.set('variant', variant.value);
          if (next.getAttribute('href') !== url.pathname + url.search) next.setAttribute('href', url.pathname + url.search);
          if (next.hidden) {
            next.hidden = false;
            reveal = true;
          }
          // The button is the last thing on the panel's first screen: bring the link under it into view
          if (reveal && next.scrollIntoView) next.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        };
        if (this.soldOutObserver) this.soldOutObserver.disconnect();
        this.soldOutObserver = new MutationObserver(update);
        this.soldOutObserver.observe(this.modalContent, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
        update();
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        this.quietVideos();
      }

      /*
        AFTER FIVE: no product video loads or plays by itself in the panel (plan decision "Card
        video"). The panel shows only the first gallery item, so:
        - a video in any other gallery item, or inside a .quick-add-hidden block, loses its sources
          and poster (it is never on screen here; with them it downloaded a 1080p stream at 0 x 0);
        - a video that is the first gallery item keeps its sources, starts as a poster and gets its
          pause / play button back (the product page hides that button in the panel).
        Runs on the fetched section before it is inserted, so nothing is requested.
      */
      quietVideos() {
        this.productElement.querySelectorAll('video').forEach((video) => {
          if (video.closest('.af-card')) return; // product cards fetch their video only when played
          const item = video.closest('.product__media-item');
          const hidden = video.closest('.quick-add-hidden');
          if (!item && !hidden) return;
          video.removeAttribute('autoplay');
          video.setAttribute('preload', 'none');
          if (item && !hidden && item.matches(':first-child')) {
            const toggle = item.querySelector('[data-af-video-toggle]');
            if (toggle) toggle.classList.remove('quick-add-hidden');
            else video.setAttribute('controls', '');
          } else {
            video.removeAttribute('src');
            video.removeAttribute('poster');
            video.querySelectorAll('source').forEach((source) => source.remove());
          }
        });
      }

      /*
        AFTER FIVE: the panel's star rating is Klaviyo Reviews' widget. Klaviyo fills the widgets it
        finds when it starts; when that start came from its 5-second timer it does not look again, so
        the panel, which arrives later, kept an empty rating. renderWidgets() is Klaviyo's own entry
        point and fills only what is still empty.
      */
      askForRating() {
        const reviews = window.klReviewsObject;
        if (reviews && typeof reviews.renderWidgets === 'function') reviews.renderWidgets();
      }

      /*
        AFTER FIVE: focus stays in the open panel and Escape always closes it (WCAG 2.4.3, 2.4.11).
        The theme's trap (trapFocus in global.js) fixes its first and last element when the panel
        opens; here the last one is inside a .quick-add-hidden block, which can never take focus, so
        Tab walked on to the page behind the overlay. This handler replaces it for the panel: it
        looks at what is focusable and visible at the moment of each key press (the variant picker
        re-renders), and it also answers when focus has ended up outside the panel.
      */
      keepFocusInside() {
        removeTrapFocus();
        document.addEventListener('keydown', this.onDocumentKeydown, true);
      }

      tabStops() {
        return getFocusableElements(this).filter((element) => {
          if (element.tabIndex < 0 || !element.getClientRects().length) return false;
          if (getComputedStyle(element).visibility === 'hidden') return false;
          // A radio group is one tab stop: its checked option
          if (element.type === 'radio' && !element.checked && element.name) {
            const group = this.querySelectorAll('input[type="radio"]:checked');
            for (let i = 0; i < group.length; i++) if (group[i].name === element.name) return false;
          }
          return true;
        });
      }

      onDocumentKeydown(event) {
        if (!this.hasAttribute('open')) return;
        const isTab = event.key === 'Tab';
        const isEscape = event.key === 'Escape';
        if (!isTab && !isEscape) return;

        const active = document.activeElement;
        const inside = !!active && this.contains(active);
        if (!inside && active && active !== document.body) {
          // Another layer opened over the panel (cart drawer, a pop-up): its keys are its own
          if (active.closest('[role="dialog"], [aria-modal="true"], dialog, cart-drawer, cart-notification')) return;
        }

        if (isEscape) {
          // With focus inside, the theme's modal closes on keyup
          if (!inside) this.hide();
          return;
        }

        const stops = this.tabStops();
        if (!stops.length) return;
        const first = stops[0];
        const last = stops[stops.length - 1];
        const dialog = this.querySelector('[role="dialog"]');
        let target = null;
        if (!inside) target = event.shiftKey ? last : first;
        else if (event.shiftKey && (active === first || active === dialog || active === this)) target = last;
        else if (!event.shiftKey && active === last) target = first;
        if (!target) return;
        event.preventDefault();
        target.focus();
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
